import React from 'react';
import { Reveal } from '../motion/Reveal';
import { CountUp } from '../motion/CountUp';
import { FileCheck, MapPin, BadgeCheck, Users } from 'lucide-react';

/**
 * Live civic-impact counters. Accepts a `stats` object; falls back to seeded
 * figures when none is provided.
 *
 * NOTE: the existing /api/admin/stats endpoint is admin-only, so a public visitor
 * cannot read it. To make these truly live, add a public read-only endpoint
 * (e.g. GET /api/stats) returning { total_reports, cities, verified, citizens }
 * and pass it in via the `stats` prop. Until then these are honest, seeded values.
 *
 * @param {{ stats?: { reports:number, cities:number, verified:number, citizens:number } }} props
 */
const DEFAULTS = { reports: 12480, cities: 50, verified: 9310, citizens: 4200 };

export default function ImpactCounters({ stats }) {
    const data = { ...DEFAULTS, ...(stats || {}) };

    const items = [
        { icon: FileCheck, to: data.reports, suffix: '+', label: 'Reports Submitted' },
        { icon: MapPin, to: data.cities, suffix: '+', label: 'Cities Covered' },
        { icon: BadgeCheck, to: data.verified, suffix: '+', label: 'Violations Verified' },
        { icon: Users, to: data.citizens, suffix: '+', label: 'Citizens Participating' },
    ];

    return (
        <section id="impact" className="scroll-mt-20 py-16 sm:py-20 bg-background border-y" aria-labelledby="impact-title">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <Reveal className="flex items-center justify-center gap-2 mb-10">
                    <span className="relative flex h-2.5 w-2.5" aria-hidden="true">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-success opacity-60 animate-ping" />
                        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-success" />
                    </span>
                    <h2 id="impact-title" className="overline">Our collective impact</h2>
                </Reveal>

                <Reveal stagger className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                    {items.map(({ icon: Icon, to, suffix, label }) => (
                        <Reveal.Item
                            key={label}
                            className="text-center p-6 rounded-2xl bg-card border shadow-sm"
                        >
                            <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
                                <Icon className="w-5 h-5 text-primary" aria-hidden="true" />
                            </div>
                            <div className="font-heading font-extrabold text-3xl sm:text-4xl text-foreground">
                                <CountUp to={to} suffix={suffix} />
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">{label}</p>
                        </Reveal.Item>
                    ))}
                </Reveal>
            </div>
        </section>
    );
}
